/**
 * DistributionStatsCards - Cards de métricas principais da distribuição automática
 * 
 * Design Premium usando DashboardStatCard reutilizável
 * 
 * Exibe:
 * - Distribuídas Hoje (verde) - com breakdown ind/grupo
 * - Agentes Online (azul)
 * - Taxa de Sucesso (dinâmica)
 * - Aguardando Distribuição (amarelo/vermelho) - com breakdown ind/grupo
 * - Tempo Médio de Processamento (roxo)
 * 
 * @version 1.2.0
 * @date 2026-01-07
 */

import { CheckCircle, Clock, AlertTriangle, Gauge, Users } from 'lucide-react';
import { DashboardStatCard } from '@/components/dashboard/DashboardStatCard';
import type { DistributionStats } from '@/hooks/useDistributionMonitor';

// ==============================================================
// Interfaces
// ==============================================================

interface DistributionStatsCardsProps {
  /** Métricas de distribuição */
  stats: DistributionStats;
  /** Indica se está carregando */
  isLoading?: boolean;
}

// ==============================================================
// Componente Principal
// ==============================================================

// ==============================================================
// Componente Principal
// ==============================================================

export function DistributionStatsCards({ stats, isLoading }: DistributionStatsCardsProps) {
  // Determina variante dinâmica para taxa de sucesso
  const getSuccessRateVariant = (): 'green' | 'yellow' | 'destructive' => {
    if (stats.successRate >= 90) return 'green';
    if (stats.successRate >= 70) return 'yellow';
    return 'destructive';
  };

  // Determina variante para aguardando distribuição
  const getPendingVariant = (): 'default' | 'yellow' | 'destructive' => {
    if (stats.pendingDistribution === 0) return 'default';
    if (stats.pendingDistribution <= 5) return 'yellow';
    return 'destructive';
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4">
      {/* Distribuídas Hoje - com breakdown por tipo */}
      <DashboardStatCard
        title="Distribuídas Hoje"
        value={stats.distributedToday.toString()}
        description={`👤 ${stats.distributedTodayIndividual} | 👥 ${stats.distributedTodayGroup}`}
        icon={CheckCircle}
        variant="green"
        isLoading={isLoading}
      />

      {/* Agentes Online */}
      <DashboardStatCard
        title="Agentes Online"
        value={stats.agentsOnline.toString()}
        description="Disponíveis agora"
        icon={Users}
        variant="blue"
        isLoading={isLoading}
      />

      {/* Taxa de Sucesso */}
      <DashboardStatCard
        title="Taxa de Sucesso"
        value={`${stats.successRate}%`}
        description="Distribuições OK"
        icon={CheckCircle}
        variant={getSuccessRateVariant()}
        isLoading={isLoading}
      />

      {/* Aguardando Distribuição - com breakdown por tipo */}
      <DashboardStatCard
        title="Aguardando"
        value={stats.pendingDistribution.toString()}
        description={
          stats.pendingDistribution > 0 
            ? `👤 ${stats.pendingIndividual} | 👥 ${stats.pendingGroup}`
            : "Sem agente atribuído"
        }
        icon={stats.pendingDistribution > 0 ? AlertTriangle : Clock}
        variant={getPendingVariant()}
        isLoading={isLoading}
      />

      {/* Tempo Médio de Processamento */}
      <DashboardStatCard
        title="Tempo Médio"
        value={`${stats.averageProcessingTime}ms`}
        description="Por distribuição"
        icon={Gauge}
        variant="purple"
        isLoading={isLoading}
      />
    </div>
  );
}
